import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Layout from '../components/Layout';
import VisitingCardDisplay from '../components/VisitingCardDisplay';
import SignInModal from '../components/SignInModal';
import { useAuth } from '../contexts/AuthContext';
import { useUserCard } from '../hooks/useUserCard';
import { formatCurrency, formatAmount } from '../utils/numberFormat';
import { 
  ArrowRight, 
  CreditCard, 
  Users, 
  TrendingUp, 
  Sparkles,
  Bell,
  CheckCircle2,
  Loader2,
  ChevronDown
} from 'lucide-react';

// Helper function to get gradient style for pipeline stages based on actual stage color
const getStageGradientStyle = (color) => {
  // Check if it's a gray/light color (last stage default)
  const grayColors = ['#CCCCCC', '#cccccc', '#e5e7eb', '#E5E7EB', '#d1d5db', '#D1D5DB'];
  if (grayColors.includes(color)) {
    return {
      background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)'
    };
  }
  // Use the actual stage color to create a gradient
  return {
    background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`
  };
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { userCard, loading: cardLoading } = useUserCard();
  
  const [myCards, setMyCards] = useState([]);
  const [myContacts, setMyContacts] = useState([]);
  const [scannedContacts, setScannedContacts] = useState([]);
  const [stats, setStats] = useState({
    totalCards: 0,
    totalContacts: 0,
    totalViews: 0
  });
  const [loading, setLoading] = useState(true);
  
  // Sign in modal state
  const [showSignInModal, setShowSignInModal] = useState(!user);
  
  // CRM Pipeline Stats
  const [crmStats, setCrmStats] = useState([]);
  const [crmTotalValue, setCrmTotalValue] = useState(0);
  const [crmTotalLeads, setCrmTotalLeads] = useState(0);
  const [team, setTeam] = useState(null);

  // Pipeline states
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [showPipelineSelector, setShowPipelineSelector] = useState(false);
  
  // Time filter state
  const [timeFilter, setTimeFilter] = useState('all'); // 'all', 'this_month', 'next_month', 'after_2_months'

  // Get currency symbol from team profile, default to ₹
  const getCurrencySymbol = () => {
    return team?.profile?.currency_symbol || '₹';
  };

  // Load pipelines
  const loadPipelines = async () => {
    try {
      const { crmAPI } = await import('../services/api');
      const pipelinesData = await crmAPI.getPipelines();
      setPipelines(pipelinesData);
      
      // Set selected pipeline to active one
      const active = pipelinesData.find(p => p.is_active);
      setSelectedPipeline(active || pipelinesData[0] || null);
    } catch (error) {
      console.error('Error loading pipelines:', error);
    }
  };

  // Switch pipeline and reload stats
  const handleSwitchPipeline = async (pipelineId) => {
    try {
      const { crmAPI } = await import('../services/api');
      await crmAPI.activatePipeline(pipelineId);
      const pipeline = pipelines.find(p => p.id === pipelineId);
      setSelectedPipeline(pipeline);
      setShowPipelineSelector(false);
      // Reload dashboard data for new pipeline
      window.location.reload();
    } catch (error) {
      console.error('Error switching pipeline:', error);
    }
  };

  // Load pipelines on mount
  useEffect(() => {
    if (user) {
      loadPipelines();
    }
  }, [user]);

  // Close pipeline selector on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPipelineSelector && !event.target.closest('.pipeline-selector')) {
        setShowPipelineSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPipelineSelector]);

  // Listen for real-time updates from other components
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'card_updated' || e.key === 'contact_updated') {
        // Trigger refetch
        if (user) {
          fetchDashboardData();
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user]);

  // Fetch data with caching (only after pipeline is loaded)
  useEffect(() => {
    // Wait for selectedPipeline to be set before fetching data
    // This prevents showing all deals before pipeline filter is applied
    if (selectedPipeline) {
      fetchDashboardData();
    }
  }, [user, timeFilter, selectedPipeline]);
  
  const fetchDashboardData = async () => {
      if (!user) {
        navigate('/');
        return;
      }
      
      // Wait for pipeline to be loaded before fetching
      if (!selectedPipeline) {
        console.log('⏳ Waiting for pipeline to load before fetching dashboard data...');
        return;
      }

      setLoading(true);
      try {
        // Use cached API for dashboard data
        const { cachedCardAPI, cachedContactAPI } = await import('../services/cachedApi');
        const { cardAPI, contactAPI, scannedContactAPI, analyticsAPI } = await import('../services/api');
        
        // Fetch top 3 cards using optimized dashboard endpoint (CACHED)
        const cardsData = await cachedCardAPI.getDashboardTopCards(user.uid);
        setMyCards(cardsData);
        
        // Fetch top 3 contacts using optimized dashboard endpoint (CACHED)
        const contactsData = await cachedContactAPI.getDashboardTopContacts(user.uid);
        
        // Separate digital and scanned contacts
        const digitalContacts = contactsData.filter(c => c.type === 'digital');
        const scannedData = contactsData.filter(c => c.type === 'scanned');
        
        setMyContacts(digitalContacts);
        setScannedContacts(scannedData);
        
        // Fetch full counts and total views for stats (CACHED)
        const allCards = await cachedCardAPI.getUserCards(user.uid);
        const allContactsResponse = await cachedContactAPI.getUserContacts(user.uid);
        const allScannedResponse = await scannedContactAPI.getAll();
        
        // Also fetch relationship contacts for accurate count
        const { contactRelationshipAPI } = await import('../services/api');
        const relationshipContacts = await contactRelationshipAPI.getUserRelationships(user.uid).catch(() => []);
        
        // Calculate total views across all cards
        let totalViews = 0;
        for (const card of allCards) {
          try {
            // Get card ID - convert _id to string if needed
            const cardId = card.id || (typeof card._id === 'object' ? String(card._id) : card._id);
            const viewsData = await analyticsAPI.getCardViews(cardId);
            totalViews += viewsData.totalViews || 0;
          } catch (error) {
            console.error('Error fetching views:', error);
          }
        }
        
        // Set stats - Combine all contact types
        // 1. Old digital contacts (backward compatibility)
        const oldDigitalContactsCount = Array.isArray(allContactsResponse) ? allContactsResponse.length : 0;
        // 2. New relationship contacts
        const relationshipContactsCount = Array.isArray(relationshipContacts) ? relationshipContacts.length : 0;
        // 3. Scanned contacts
        const scannedContactsCount = allScannedResponse?.total || allScannedResponse?.data?.length || 0;
        
        // Avoid double-counting: filter out old contacts that are now in relationships
        const relationshipUsernames = new Set(relationshipContacts.map(c => c.username));
        const uniqueOldContactsCount = Array.isArray(allContactsResponse) 
          ? allContactsResponse.filter(c => !relationshipUsernames.has(c.username)).length 
          : 0;
        
        setStats({
          totalCards: allCards.length,
          totalContacts: uniqueOldContactsCount + relationshipContactsCount + scannedContactsCount,
          totalViews
        });

        // Fetch CRM stats
        try {
          const { crmAPI, teamAPI } = await import('../services/api');
          
          // Fetch team data to check member settings
          let teamData = null;
          try {
            teamData = await teamAPI.getMyTeam();
            setTeam(teamData);
          } catch (error) {
            console.log('No team data');
          }
          
          const [stages, allLeads] = await Promise.all([
            crmAPI.getStages(),
            teamAPI.getTeamLeads()
          ]);
          
          // Filter by selected pipeline first
          let filteredLeads = allLeads;
          if (selectedPipeline) {
            filteredLeads = allLeads.filter(lead => lead.pipelineId === selectedPipeline.id);
            console.log(`🔍 Pipeline filter applied: ${filteredLeads.length}/${allLeads.length} leads (pipeline: ${selectedPipeline.id})`);
          }
          
          // Apply privacy filters for staff members
          if (teamData && teamData.my_role === 'staff' && teamData.my_settings) {
            console.log('📊 Applying dashboard filters:', {
              role: teamData.my_role,
              deal_visibility: teamData.my_settings.deal_visibility,
              amount_visibility: teamData.my_settings.amount_visibility,
              current_user: user.uid,
              total_leads: allLeads.length
            });
            
            // Filter based on deal_visibility setting
            if (teamData.my_settings.deal_visibility === 'assigned_only') {
              filteredLeads = filteredLeads.filter(lead => lead.assigned_to === user.uid);
              console.log(`✅ Filtered to ${filteredLeads.length} assigned leads`);
            }
            
            // For amount_visibility, we need to filter amounts in calculation below
            // If amount_visibility = "assigned_only", only show amounts for assigned leads
          }
          
          // Apply time-based filter (closing date)
          if (timeFilter !== 'all') {
            const beforeTimeFilter = filteredLeads.length;
            const now = new Date();
            const currentYear = now.getFullYear();
            const currentMonth = now.getMonth(); // 0-indexed (0 = Jan, 11 = Dec)
            
            filteredLeads = filteredLeads.filter(lead => {
              // If no closing date, exclude from time filters
              if (!lead.closingDate) return false;
              
              const closingDate = new Date(lead.closingDate);
              const closingYear = closingDate.getFullYear();
              const closingMonth = closingDate.getMonth();
              
              if (timeFilter === 'this_month') {
                return closingYear === currentYear && closingMonth === currentMonth;
              } else if (timeFilter === 'next_month') {
                const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
                const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
                return closingYear === nextYear && closingMonth === nextMonth;
              } else if (timeFilter === 'after_2_months') {
                const twoMonthsLater = new Date(currentYear, currentMonth + 2, 1);
                return closingDate >= twoMonthsLater;
              }
              
              return true;
            });
            
            console.log(`📅 Time filter applied: ${timeFilter}, ${filteredLeads.length}/${beforeTimeFilter} deals`);
          }
          
          // Filter stages to only show stages from selected pipeline
          const pipelineStages = selectedPipeline 
            ? stages.filter(stage => stage.pipelineId === selectedPipeline.id)
            : stages;
          
          // Calculate stats per stage (only from filteredLeads)
          const statsWithCounts = pipelineStages.map(stage => {
            const stageLeads = filteredLeads.filter(lead => lead.stageId === stage.id);
            
            // Calculate total value - only from leads in filteredLeads (already filtered by assignment)
            let totalValue = 0;
            
            // For staff with amount_visibility = "assigned_only"
            if (teamData && teamData.my_role === 'staff' && teamData.my_settings) {
              if (teamData.my_settings.amount_visibility === 'assigned_only') {
                // Only show amounts for leads assigned to this user
                totalValue = stageLeads.reduce((sum, lead) => {
                  // Double-check: only count if assigned to current user
                  if (lead.assigned_to === user.uid && lead.dealAmount) {
                    const amount = parseFloat(lead.dealAmount) || 0;
                    // Convert if custom currency
                    if (lead.currency && lead.currencyRate) {
                      return sum + (amount * parseFloat(lead.currencyRate));
                    }
                    return sum + amount;
                  }
                  return sum;
                }, 0);
              } else {
                // Show amounts for all leads in filteredLeads (staff can see all amounts)
                totalValue = stageLeads.reduce((sum, lead) => {
                  const amount = parseFloat(lead.dealAmount) || 0;
                  // Convert if custom currency
                  if (lead.currency && lead.currencyRate) {
                    return sum + (amount * parseFloat(lead.currencyRate));
                  }
                  return sum + amount;
                }, 0);
              }
            } else {
              // Owner or no team - show all amounts from filteredLeads
              totalValue = stageLeads.reduce((sum, lead) => {
                const amount = parseFloat(lead.dealAmount) || 0;
                // Convert if custom currency
                if (lead.currency && lead.currencyRate) {
                  return sum + (amount * parseFloat(lead.currencyRate));
                }
                return sum + amount;
              }, 0);
            }
            
            console.log(`📈 Stage "${stage.stageName}": ${stageLeads.length} leads, ${getCurrencySymbol()}${totalValue}`);
            
            return {
              ...stage,
              count: stageLeads.length,
              totalValue: totalValue
            };
          });
          
          setCrmStats(statsWithCounts);
          setCrmTotalValue(statsWithCounts.reduce((sum, stage) => sum + stage.totalValue, 0));
          setCrmTotalLeads(statsWithCounts.reduce((sum, stage) => sum + stage.count, 0));
        } catch (error) {
          console.error('Error fetching CRM stats:', error);
          setCrmStats([]);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

  // Show sign in modal if not logged in
  useEffect(() => {
    if (!user) {
      setShowSignInModal(true);
    }
  }, [user]);

  const allContacts = [...myContacts, ...scannedContacts].slice(0, 3);

  return (
    <Layout userCard={userCard} loading={cardLoading}>
      <div className="py-4 sm:py-6 px-3 sm:px-4 lg:px-6 bg-gray-50 min-h-screen">
        {user ? (
          <div className="max-w-7xl mx-auto">
            {/* Welcome Section */}
            <div className="mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Welcome back, {user.displayName || 'User'}! 👋
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                Here&apos;s what&apos;s happening with your cards and contacts
              </p>
            </div>

          {/* Overview Section */}
          <Card className="mb-6 sm:mb-8 border border-gray-200 shadow-sm bg-white">
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-4 mb-4 sm:mb-6">
                {/* Title and Stats Row */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Overview</h2>
                    {!loading && crmStats.length > 0 && (
                      <p className="text-xs sm:text-sm text-gray-600">
                        {crmTotalLeads} leads
                        {crmTotalValue > 0 && (
                          <> • {formatCurrency(crmTotalValue, getCurrencySymbol())} total pipeline value</>
                        )}
                      </p>
                    )}
                  </div>
                  <Link to="/crm" className="w-full sm:w-auto">
                    <Button className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto">
                      <span className="hidden sm:inline">View CRM</span>
                      <span className="sm:hidden">View</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>

                {/* Filters Row - Better Mobile Layout */}
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Pipeline Selector */}
                  {selectedPipeline && pipelines.length > 0 && (
                    <div className="relative pipeline-selector flex-1 sm:flex-initial">
                      <button
                        onClick={() => setShowPipelineSelector(!showPipelineSelector)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gradient-to-r from-amber-100 to-teal-100 border border-amber-300 rounded-lg hover:border-amber-400 hover:shadow-md transition-all"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="w-2 h-2 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 flex-shrink-0"></div>
                          <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                            {selectedPipeline.name}
                          </span>
                        </div>
                        <ChevronDown className="w-4 h-4 text-gray-600 flex-shrink-0" />
                      </button>

                      {/* Pipeline Dropdown */}
                      {showPipelineSelector && (
                        <div className="absolute top-full left-0 right-0 sm:left-0 sm:right-auto sm:w-72 mt-2 bg-white rounded-lg shadow-xl border-2 border-gray-200 z-50">
                          <div className="p-3 border-b border-gray-200">
                            <p className="text-xs font-semibold text-gray-500 uppercase">Select Pipeline</p>
                          </div>
                          <div className="max-h-80 overflow-y-auto">
                            {pipelines.map((pipeline) => (
                              <button
                                key={pipeline.id}
                                onClick={() => handleSwitchPipeline(pipeline.id)}
                                className={`w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors ${
                                  pipeline.id === selectedPipeline.id ? 'bg-amber-50' : ''
                                }`}
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div 
                                    className={`w-3 h-3 rounded-full flex-shrink-0 ${
                                      pipeline.id === selectedPipeline.id 
                                        ? 'bg-gradient-to-r from-amber-500 to-yellow-500 ring-2 ring-amber-300 ring-offset-2' 
                                        : 'bg-gray-300'
                                    }`}
                                  ></div>
                                  <div className="text-left min-w-0 flex-1">
                                    <p className="font-semibold text-gray-900 text-sm truncate">{pipeline.name}</p>
                                    <p className="text-xs text-gray-500 truncate">{pipeline.description}</p>
                                  </div>
                                </div>
                                {pipeline.id === selectedPipeline.id && (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full flex-shrink-0 ml-2">
                                    Active
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Time Filter Dropdown */}
                  <select
                    value={timeFilter}
                    onChange={(e) => setTimeFilter(e.target.value)}
                    className={`w-full sm:w-auto px-3 py-2 text-xs sm:text-sm border rounded-lg transition-all ${
                      timeFilter === 'all'
                        ? 'border-gray-300 bg-white hover:bg-gray-50'
                        : 'border-gray-900 bg-gray-100 text-gray-900 font-medium'
                    }`}
                  >
                    <option value="all">All Months</option>
                    <option value="this_month">
                      This Month ({new Date().toLocaleDateString('en-US', { month: 'short' })})
                    </option>
                    <option value="next_month">
                      Next Month ({new Date(new Date().setMonth(new Date().getMonth() + 1)).toLocaleDateString('en-US', { month: 'short' })})
                    </option>
                    <option value="after_2_months">After 2 Months</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8 sm:py-12">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-sm sm:text-base text-gray-600">Loading pipeline stats...</p>
                </div>
              ) : crmStats.length > 0 ? (
                <>
                  {/* Pipeline Stages Grid - Professional Clean Design */}
                  <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                  {crmStats.map((stage, index) => (
                    <Link
                      key={stage.id}
                      to="/crm"
                      className="group"
                    >
                      <div className="relative overflow-hidden rounded-lg shadow-sm hover:shadow-md transition-all duration-300 min-h-[120px] flex flex-col">
                        {/* Gradient Header - uses actual stage color */}
                        <div 
                          className="h-2 w-full rounded-t-lg"
                          style={getStageGradientStyle(stage.color)}
                        ></div>
                        
                        <div className="p-3 sm:p-4 flex flex-col flex-1 bg-white border border-t-0 border-gray-200 rounded-b-lg">
                          {/* Stage Name */}
                          <h3 
                            className="font-bold text-gray-900 text-sm sm:text-base mb-2 line-clamp-2"
                            title={stage.name}
                          >
                            {stage.name}
                          </h3>
                          
                          {/* Count */}
                          <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                            <span className="font-bold text-2xl sm:text-3xl text-gray-900">
                              {stage.count}
                            </span>
                            <span className="text-gray-500 text-xs sm:text-sm whitespace-nowrap">
                              {stage.count === 1 ? 'lead' : 'leads'}
                            </span>
                          </div>
                          
                          {/* Deal Value - only show if value > 0 */}
                          {stage.totalValue > 0 && (
                            <div className="inline-block px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-gray-100 text-gray-700 mt-auto">
                              <span className="truncate block">
                                {formatCurrency(stage.totalValue, getCurrencySymbol())}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Arrow Icon */}
                        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pipeline Summary Bar - Optimized for Mobile */}
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-white rounded-lg border border-gray-200">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm">
                    <div className="flex items-center justify-between sm:justify-start gap-2">
                      <span className="text-gray-600">Total Leads:</span>
                      <span 
                        className="font-bold text-gray-900 cursor-default"
                        title={crmTotalLeads.toLocaleString('en-IN')}
                      >
                        {crmTotalLeads}
                      </span>
                    </div>
                    {crmTotalValue > 0 && (
                      <>
                        <div className="flex items-center justify-between sm:justify-start gap-2">
                          <span className="text-gray-600">Pipeline Value:</span>
                          <span 
                            className="font-bold text-green-600 cursor-default"
                            title={`${getCurrencySymbol()}${crmTotalValue.toLocaleString('en-IN')}`}
                          >
                            {formatCurrency(crmTotalValue, getCurrencySymbol())}
                          </span>
                        </div>
                        <div className="flex items-center justify-between sm:justify-start gap-2">
                          <span className="text-gray-600">Avg Deal Size:</span>
                          <span 
                            className="font-bold text-amber-600 cursor-default"
                            title={`${getCurrencySymbol()}${Math.round(crmTotalValue / crmTotalLeads).toLocaleString('en-IN')}`}
                          >
                            {formatCurrency(Math.round(crmTotalValue / crmTotalLeads), getCurrencySymbol())}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                </>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <p className="text-sm sm:text-base text-gray-600 mb-4">No CRM data yet. Start adding leads to your pipeline!</p>
                  <Link to="/crm">
                    <Button className="bg-gray-900 hover:bg-gray-800 text-white">
                      Go to CRM
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
            {/* Quick Actions - Professional Design */}
            <Card className="border border-gray-200 shadow-sm bg-white">
              <CardContent className="p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Quick Actions</h3>
                <div className="space-y-2 sm:space-y-3">
                  <Button
                    onClick={() => navigate('/create')}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white text-sm sm:text-base"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Create New Card
                  </Button>
                  
                  <Button
                    onClick={() => navigate('/contacts?scan=true')}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white text-sm sm:text-base"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Scan Business Card
                  </Button>
                  
                  <Button
                    onClick={() => navigate('/contacts')}
                    variant="outline"
                    className="w-full text-sm sm:text-base border-gray-300 hover:bg-gray-50"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Add Contact
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* What's New Section - Professional Design */}
              <Card className="border border-gray-200 bg-white shadow-sm">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-3 sm:mb-4">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-900 rounded-lg flex items-center justify-center">
                      <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-900">What&apos;s New</h3>
                  </div>
                  
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Advanced Analytics Dashboard</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Advanced analytics dashboard with detailed insights
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Side Menu Navigation</p>
                        <p className="text-xs text-gray-600 mt-1">
                          New sidebar design for better navigation experience
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">CSV Export</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Export all your contacts to CSV for easy backup
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Physical Card Scanner</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Scan business cards with AI-powered OCR and save contacts instantly
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">CRM & Sales Pipeline</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Full CRM system with pipeline management, lead tracking, and deal conversion
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <Bell className="w-5 h-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Coming Soon: CRM Reports</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Advanced analytics and reporting for your sales pipeline with performance insights
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
          </div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
              <Sparkles className="w-16 h-16 text-amber-500 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Piper Card Dashboard</h2>
              <p className="text-gray-600 mb-6">Please sign in to view your dashboard</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Sign In Modal */}
      <SignInModal 
        open={showSignInModal} 
        onOpenChange={setShowSignInModal}
      />
    </Layout>
  );
};

export default Dashboard;
